import { CERTIFICATE_COLLECTION, type CollectionCertificateStyle } from "./certificateCollection";
import { lightTheme } from "../../styles/theme";
import type { SignatureInput } from "../signatures/signatures";

/** Stored ids. "elegant" is shown as Floral; the id stays so saved templates keep working. */
export type CertificateStyle =
  | CollectionCertificateStyle
  | "champion"
  | "classic"
  | "modern"
  | "elegant"
  | "playful"
  | "ornate"
  | "schoolbooks"
  | "prestige"
  | "bumblebee"
  | "geometric"
  | "rainbow"
  | "lagoon"
  | "breeze"
  | "purple-botanical"
  | "modern-purple"
  | "elegant-purple"
  | "playful-purple"
  | "royal-purple"
  | "modern-angles-purple";

export type CertificateFontId =
  | "playfair"
  | "lora"
  | "montserrat"
  | "raleway"
  | "great-vibes"
  | "nunito"
  | "unifraktur"
  | "oswald";

export type CertificateTemplateStatus = "draft" | "published" | "archived";

export type CertificateOrientation = "landscape" | "portrait";

/** The saved design. Always "certificate template" in full: "Template" means notice Templates. */
export type CertificateTemplate = {
  certificateTemplateId: string;
  name: string;
  style: CertificateStyle;
  primaryColour: string;
  accentColour: string;
  font: CertificateFontId;
  /** Missing on certificate templates saved before it existed: landscape. */
  orientation?: CertificateOrientation;
  /** The tint of the paper, e.g. cream. Missing on older ones: white. */
  paperColour?: string;
  logoUrl?: string;
  organisationName: string;
  introText: string;
  status: CertificateTemplateStatus;
  isBuiltIn?: boolean;
};

const CERTIFICATE_TEMPLATE_SETTING_KEYS = [
  "name",
  "style",
  "primaryColour",
  "accentColour",
  "font",
  "orientation",
  "paperColour",
  "logoUrl",
  "organisationName",
  "introText",
] as const;

/** The parts of a certificate template a Team member edits and the server stores as-is. */
export type CertificateTemplateSettings = Omit<
  Pick<CertificateTemplate, (typeof CERTIFICATE_TEMPLATE_SETTING_KEYS)[number]>,
  "orientation" | "paperColour"
> & {
  orientation: CertificateOrientation;
  paperColour: string;
};

export const CERTIFICATE_ORIENTATIONS: { id: CertificateOrientation; label: string }[] = [
  { id: "landscape", label: "Landscape" },
  { id: "portrait", label: "Portrait" },
];

export const DEFAULT_PAPER_COLOUR = "#ffffff";

/** Suggested paper tints, like the card certificates are printed on. Any colour can be picked too. */
const CREAM_PAPER = "#fff8e7";
const PALE_YELLOW_PAPER = "#fffbd9";
/** Dark papers, for designs printed in light ink. */
const CHARCOAL_PAPER = "#161616";
const NAVY_PAPER = "#0e1a33";

export const PAPER_COLOURS: { label: string; colour: string }[] = [
  { label: "White", colour: DEFAULT_PAPER_COLOUR },
  { label: "Cream", colour: CREAM_PAPER },
  { label: "Ivory", colour: "#fffdf2" },
  { label: "Pale blue", colour: "#eaf3fb" },
  { label: "Pale yellow", colour: PALE_YELLOW_PAPER },
  { label: "Pale green", colour: "#edf7ec" },
  { label: "Charcoal", colour: CHARCOAL_PAPER },
  { label: "Navy", colour: NAVY_PAPER },
];

export function certificateOrientation(
  template: Pick<CertificateTemplate, "orientation">,
): CertificateOrientation {
  return template.orientation ?? "landscape";
}

export function certificatePaperColour(
  template: Pick<CertificateTemplate, "paperColour">,
): string {
  return template.paperColour ?? DEFAULT_PAPER_COLOUR;
}

/**
 * True when light ink reads better than dark ink on this paper, judged by its
 * relative luminance. Anything that isn't a 6-digit hex counts as light.
 */
export function isDarkPaper(paperColour: string): boolean {
  const hex = /^#([0-9a-f]{6})$/i.exec(paperColour.trim())?.[1];
  if (!hex) return false;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const channel = parseInt(hex.slice(i, i + 2), 16) / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.18;
}

/** A4 at 96 dpi, the size a certificate is laid out at. */
export function certificatePageSize(orientation: CertificateOrientation): {
  width: number;
  height: number;
} {
  const long = 1123;
  const short = 794;
  return orientation === "portrait"
    ? { width: short, height: long }
    : { width: long, height: short };
}

/** The setting that holds an uploaded image. */
export type CertificateTemplateImageField = "logoUrl";

/** What an Award prints on every certificate it hands out. */
export type CertificateAwardDetails = {
  certificateTitle?: string;
  achievementName?: string;
  description?: string;
  /** "YYYY-MM-DD" */
  completionDate?: string;
  /** One or two people who sign it, copied onto the Award. */
  signatures?: SignatureInput[];
};

/** The one child a certificate is for. */
export type CertificateRecipient = {
  firstName?: string;
  surname?: string;
  certificateId?: string;
};

export const CERTIFICATE_STYLES: { id: CertificateStyle; label: string }[] = [
  { id: "champion", label: "Champion" },
  { id: "classic", label: "Classic" },
  { id: "modern", label: "Modern" },
  { id: "elegant", label: "Floral" },
  { id: "playful", label: "Playful" },
  { id: "ornate", label: "Ornate" },
  { id: "schoolbooks", label: "Schoolbooks" },
  { id: "prestige", label: "Prestige" },
  { id: "bumblebee", label: "Bumblebee" },
  { id: "geometric", label: "Geometric" },
  { id: "rainbow", label: "Rainbow" },
  { id: "lagoon", label: "Lagoon" },
  { id: "breeze", label: "Breeze" },
  { id: "purple-botanical", label: "Purple Botanical" },
  { id: "modern-purple", label: "Modern Purple" },
  { id: "elegant-purple", label: "Elegant Purple" },
  { id: "playful-purple", label: "Playful Purple" },
  { id: "royal-purple", label: "Royal Purple" },
  { id: "modern-angles-purple", label: "Modern Angles Purple" },

  ...CERTIFICATE_COLLECTION.map(({ id, label }) => ({ id, label })),
];

/** What sort of lettering a font is, which decides how big and bold it is drawn. */
export type CertificateFontKind =
  | "serif"
  | "sans"
  | "script"
  | "rounded"
  | "blackletter"
  | "condensed";

export type CertificateFont = {
  id: CertificateFontId;
  label: string;
  kind: CertificateFontKind;
  /** CSS font-family stack. */
  family: string;
};

export const CERTIFICATE_FONTS: CertificateFont[] = [
  {
    id: "playfair",
    label: "Playfair Display",
    kind: "serif",
    family: '"Playfair Display", Georgia, serif',
  },
  { id: "lora", label: "Lora", kind: "serif", family: "Lora, Georgia, serif" },
  {
    id: "montserrat",
    label: "Montserrat",
    kind: "sans",
    family: "Montserrat, Arial, sans-serif",
  },
  {
    id: "raleway",
    label: "Raleway",
    kind: "sans",
    family: "Raleway, Arial, sans-serif",
  },
  {
    id: "great-vibes",
    label: "Great Vibes",
    kind: "script",
    family: '"Great Vibes", cursive',
  },
  {
    id: "nunito",
    label: "Nunito",
    kind: "rounded",
    family: "Nunito, Arial, sans-serif",
  },
  {
    id: "unifraktur",
    label: "Old English",
    kind: "blackletter",
    family: '"UnifrakturMaguntia", "Old English Text MT", serif',
  },
  {
    id: "oswald",
    label: "Tall capitals (Oswald)",
    kind: "condensed",
    family: 'Oswald, "Arial Narrow", sans-serif',
  },
];

export function certificateFontFamily(font: CertificateFontId): string {
  return (
    CERTIFICATE_FONTS.find((f) => f.id === font)?.family ??
    CERTIFICATE_FONTS[0].family
  );
}

export function certificateFontKind(font: CertificateFontId): CertificateFontKind {
  return CERTIFICATE_FONTS.find((f) => f.id === font)?.kind ?? "serif";
}

/** What picking a style fills in. The Team member can change any of it after. */
export type CertificateLook = Pick<
  CertificateTemplateSettings,
  "primaryColour" | "accentColour" | "font" | "paperColour"
>;

const SUGGESTED_LOOKS: Record<CertificateStyle, CertificateLook> = {
  ...Object.fromEntries(CERTIFICATE_COLLECTION.map(item => [item.id, { primaryColour: item.primary, accentColour: item.accent, font: item.font, paperColour: item.paper }])) as Record<CollectionCertificateStyle, CertificateLook>,
  "purple-botanical": {
    primaryColour: "#35105c",
    accentColour: "#9b6ac6",
    font: "playfair",
    paperColour: "#fdfbff",
  },
  "modern-purple": {
    primaryColour: "#35105c",
    accentColour: "#9b6ac6",
    font: "playfair",
    paperColour: "#fdfbff",
  },
  "elegant-purple": {
    primaryColour: "#35105c",
    accentColour: "#dcae38",
    font: "playfair",
    paperColour: "#fdfbff",
  },
  "playful-purple": {
    primaryColour: "#35105c",
    accentColour: "#9b6ac6",
    font: "playfair",
    paperColour: "#fdfbff",
  },
  "royal-purple": {
    primaryColour: "#f5cd55",
    accentColour: "#dcae38",
    font: "playfair",
    paperColour: "#2c1046",
  },
  "modern-angles-purple": {
    primaryColour: "#35105c",
    accentColour: "#9b6ac6",
    font: "playfair",
    paperColour: "#fdfbff",
  },

  // The same as the ready-made Champion.
  champion: {
    primaryColour: "#13a3b4",
    accentColour: "#5fd3dc",
    font: "montserrat",
    paperColour: DEFAULT_PAPER_COLOUR,
  },
  // The same as the ready-made Classic.
  classic: {
    primaryColour: lightTheme.colors.primaryDark.toLowerCase(),
    accentColour: lightTheme.colors.gold.toLowerCase(),
    font: "playfair",
    paperColour: DEFAULT_PAPER_COLOUR,
  },
  modern: {
    primaryColour: "#0f6b5c",
    accentColour: "#8cc63f",
    font: "montserrat",
    paperColour: DEFAULT_PAPER_COLOUR,
  },
  elegant: {
    primaryColour: "#5b3a6b",
    accentColour: "#6f9a6a",
    font: "great-vibes",
    paperColour: CREAM_PAPER,
  },
  playful: {
    primaryColour: "#e4572e",
    accentColour: "#1f9fb4",
    font: "nunito",
    paperColour: PALE_YELLOW_PAPER,
  },
  ornate: {
    primaryColour: "#6b1d2a",
    accentColour: "#b08a3e",
    font: "unifraktur",
    paperColour: CREAM_PAPER,
  },
  schoolbooks: {
    primaryColour: "#2b4c9b",
    accentColour: "#f5c400",
    font: "playfair",
    paperColour: "#f8f8f6",
  },
  prestige: {
    primaryColour: "#c9a24d",
    accentColour: "#e6c977",
    font: "playfair",
    paperColour: CHARCOAL_PAPER,
  },
  bumblebee: {
    primaryColour: "#4a8fd4",
    accentColour: "#f7d84a",
    font: "nunito",
    paperColour: DEFAULT_PAPER_COLOUR,
  },
  geometric: {
    primaryColour: "#3a78d4",
    accentColour: "#f2a33a",
    font: "montserrat",
    paperColour: NAVY_PAPER,
  },
  rainbow: {
    primaryColour: "#f0a500",
    accentColour: "#1a9fc9",
    font: "nunito",
    paperColour: DEFAULT_PAPER_COLOUR,
  },
  lagoon: {
    primaryColour: "#0e8ca8",
    accentColour: "#2bbfbf",
    font: "nunito",
    paperColour: DEFAULT_PAPER_COLOUR,
  },
  breeze: {
    primaryColour: "#139fa6",
    accentColour: "#3cc8c8",
    font: "nunito",
    paperColour: DEFAULT_PAPER_COLOUR,
  },
};

export function suggestedLook(style: CertificateStyle): CertificateLook {
  return SUGGESTED_LOOKS[style];
}

export const DEFAULT_CERTIFICATE_INTRO_TEXT =
  "This certificate is proudly presented to";

/** Shown wherever an Award or recipient hasn't filled a part in yet. */
export const CERTIFICATE_SAMPLE_TEXT = {
  certificateTitle: "Certificate of Achievement",
  recipientName: "Recipient name",
  achievementName: "Achievement name",
  description: "A short line about what this certificate is for.",
  completionDate: "1 January 2026",
  certificateId: "CERT-2026-0001",
  signatureName: "Your name",
  signatureTitle: "Title",
} as const;

/** Reserved ID of a ready-made certificate template, one per style: "builtin-classic". */
function builtInCertificateTemplateId(style: CertificateStyle): string {
  return `builtin-${style}`;
}

/** The ready-made Champion, which new Awards and new certificate templates start with. */
export const BUILT_IN_CERTIFICATE_TEMPLATE_ID = builtInCertificateTemplateId("champion");

/**
 * The ready-made Classic, the default before Champion. Awards saved before
 * the other ready-made ones existed use it, and so does any Award whose
 * certificate template can't be found, so certificates already sent keep
 * their look.
 */
const BUILT_IN_CLASSIC_ID = builtInCertificateTemplateId("classic");

type CertificateOrganisation = { name?: string; logoUrl?: string };

function builtInCertificateTemplateFor(
  { id: style, label }: (typeof CERTIFICATE_STYLES)[number],
  organisation: CertificateOrganisation,
): CertificateTemplate {
  return {
    certificateTemplateId: builtInCertificateTemplateId(style),
    name: label,
    style,
    ...suggestedLook(style),
    orientation: "landscape",
    logoUrl: organisation.logoUrl?.trim() || undefined,
    organisationName: organisation.name?.trim() ?? "",
    introText: DEFAULT_CERTIFICATE_INTRO_TEXT,
    status: "published",
    isBuiltIn: true,
  };
}

/**
 * The ready-made certificate templates every Organisation gets, one per
 * style. They live in code, not in the database, and fill in the
 * Organisation's name and logo when drawn.
 */
export function builtInCertificateTemplates(
  organisation: CertificateOrganisation,
): CertificateTemplate[] {
  return CERTIFICATE_STYLES.map((style) => builtInCertificateTemplateFor(style, organisation));
}

/** The ready-made Classic, among these. */
export function builtInClassic(templates: CertificateTemplate[]): CertificateTemplate {
  return findCertificateTemplate(templates, BUILT_IN_CLASSIC_ID) ?? templates[0];
}

/** The ready-made Champion, the default. */
export function builtInCertificateTemplate(
  organisation: CertificateOrganisation,
): CertificateTemplate {
  const templates = builtInCertificateTemplates(organisation);
  return findCertificateTemplate(templates, BUILT_IN_CERTIFICATE_TEMPLATE_ID) ?? templates[0];
}

/** True for the reserved ID of one of the ready-made certificate templates. */
export function isBuiltInCertificateTemplateId(certificateTemplateId: string): boolean {
  return CERTIFICATE_STYLES.some(
    ({ id }) => builtInCertificateTemplateId(id) === certificateTemplateId,
  );
}

/** Published certificate templates (and the ready-made ones) are locked. */
export function canEditCertificateTemplate(template: CertificateTemplate): boolean {
  return !template.isBuiltIn && template.status === "draft";
}

/** Only a published certificate template of the Organisation's own can be archived. */
export function canArchiveCertificateTemplate(template: CertificateTemplate): boolean {
  return !template.isBuiltIn && template.status === "published";
}

/** The certificate template with this ID, if it's among these. */
export function findCertificateTemplate(
  templates: CertificateTemplate[],
  certificateTemplateId: string
): CertificateTemplate | undefined {
  return templates.find((template) => template.certificateTemplateId === certificateTemplateId);
}

/** New Awards can only use published certificate templates (the ready-made ones count). */
export function isOfferedForNewAwards(template: CertificateTemplate): boolean {
  return template.status === "published";
}

export function certificateTemplateSettings(
  template: CertificateTemplate
): CertificateTemplateSettings {
  const settings = {} as Record<string, unknown>;
  for (const key of CERTIFICATE_TEMPLATE_SETTING_KEYS) settings[key] = template[key];
  return {
    ...(settings as CertificateTemplateSettings),
    orientation: certificateOrientation(template),
    paperColour: certificatePaperColour(template),
  };
}

/** The settings for a new draft copied from an existing certificate template. */
export function duplicateCertificateTemplateSettings(
  template: CertificateTemplate
): CertificateTemplateSettings {
  return { ...certificateTemplateSettings(template), name: `${template.name} (copy)` };
}

/** A blank draft: the ready-made design with the Organisation's name and logo filled in. */
export function newCertificateTemplateSettings(organisation: {
  name?: string;
  logoUrl?: string;
}): CertificateTemplateSettings {
  return {
    ...certificateTemplateSettings(builtInCertificateTemplate(organisation)),
    name: "",
  };
}

/** Alternate colourways remain resolvable for certificates already saved. */
export const CERTIFICATE_COLOUR_VARIANTS: Partial<Record<CertificateStyle, CertificateStyle>> = {
  "honours-sash": "emerald-ribbon",
  "ruby-distinction": "midnight-gold",
  floodlights: "football-club",
  "silver-strategy": "chess-master",
};
/** Retired from new selections; kept in the registry to render saved certificates faithfully. */
export const RETIRED_CERTIFICATE_STYLES: readonly CertificateStyle[] = [
  "elegant", "playful", "ornate", "modern-purple", "modern-angles-purple",
  "heritage-parchment", "podium", "court-champion", "chess-master", "creative-studio",
  "botanical-laurel", "festival", "golden-current", "table-tennis", "playful-purple",
  "pencil-box", "ivory-facets", "navy-diploma",
  "bowling-lanes", "silk-ribbons", "golden-fold",
];
export const isStandaloneCertificateStyle = (style: CertificateStyle) =>
  !CERTIFICATE_COLOUR_VARIANTS[style] && !RETIRED_CERTIFICATE_STYLES.includes(style);
export const isStandaloneCertificateTemplate = (template: CertificateTemplate) => !template.isBuiltIn || isStandaloneCertificateStyle(template.style);
export function certificateColourways(style: CertificateStyle) {
  const base = CERTIFICATE_COLOUR_VARIANTS[style] ?? style;
  return CERTIFICATE_STYLES.filter(item => item.id === base || CERTIFICATE_COLOUR_VARIANTS[item.id] === base);
}
